// Dashboard section: monthly contact-card stats by status as a labeled donut,
// with a compact potential-fees and month-earnings summary.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Flex, useToken } from "@chakra-ui/react";
import { MdTrendingUp } from "react-icons/md";
import CardHeader from "../../shared/CardHeader";
import LabeledDonut from "../../shared/LabeledDonut";
import CompactFees from "../../shared/CompactFees";

/** Status segments with their stats key and Chakra color token. */
const STATUSES = [
  { label: "Acceptés", key: "accepted", token: "green.500" },
  { label: "En attente", key: "pending", token: "orange.500" },
  { label: "Refusés", key: "declined", token: "red.500" }
];

const EMPTY = { accepted: 0, pending: 0, declined: 0 };

/**
 * Displays the current month's contact-card stats as a labeled donut plus a
 * compact fees summary, filling the section height.
 * @returns {React.ReactElement}
 */
export default function ContactsSection() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(EMPTY);
  const [potentialFees, setPotentialFees] = useState([]);
  const [monthFees, setMonthFees] = useState([]);
  const colors = useToken("colors", STATUSES.map((s) => s.token));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/admin/contact_cards/income_month");
      setStats({
        accepted: data.contact_card_accepted, pending: data.contact_card_pending,
        declined: data.contact_card_declined
      });
      setPotentialFees(data.potential_fees || []);
      setMonthFees(data.month_fees || []);
    } catch (error) {
      console.error("Failed to fetch contact stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = STATUSES.map((s, i) => ({ label: s.label, value: stats[s.key], color: colors[i] }));
  const total = STATUSES.reduce((sum, s) => sum + stats[s.key], 0);
  return (
    <Flex direction="column" h="100%">
      <CardHeader icon={MdTrendingUp} title="Contacts du mois" onRefresh={load} loading={loading} />
      <Flex flex="1" align="center" justify="center" py="8px">
        <LabeledDonut data={items} total={total} centerLabel="Total" />
      </Flex>
      <CompactFees potential={potentialFees} month={monthFees} />
    </Flex>
  );
}
